import { Info } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { DisclosureChevron, useDisclosure } from "@/components/ui/Disclosure";
import { RAIL_BOX } from "./StageRail";
import { ChatMarkdown } from "@/components/chat/ChatMarkdown";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { LEGAL_DOCUMENTS, type LegalDocument } from "@/policies";

/** One policy, opened for reading beside whatever draft the attorney had open. */
function PolicySheet({
  policy,
  onClose,
}: {
  policy: LegalDocument | undefined;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={policy !== undefined}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      {policy && (
        <SheetContent className="flex w-full max-w-xl flex-col gap-4 p-6">
          <div>
            <SheetTitle>{policy.document.title}</SheetTitle>
            <SheetDescription>
              Version {policy.document.version}, effective{" "}
              {policy.document.effectiveDate}
            </SheetDescription>
          </div>
          {/* Named and focusable, so the document can be scrolled without a
              mouse. */}
          <div
            role="region"
            aria-label={policy.document.title}
            tabIndex={0}
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <ChatMarkdown content={policy.document.body} />
          </div>
          <Link
            to={`/policies/${policy.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline underline-offset-2"
          >
            Open in a new tab
          </Link>
        </SheetContent>
      )}
    </Sheet>
  );
}

/**
 * Permanent access to the legal documents, from the bottom of the stage rail.
 *
 * The rail is 224px wide, which is enough to list the documents but not to read
 * one, so choosing a document opens it in a sheet rather than expanding it in
 * place. That keeps the attorney in whatever draft they had open — the point of
 * having this in the shell at all rather than only as a page.
 *
 * Collapsed by default. It sits below the draft picker, so an always-open list
 * would push the drafts an attorney actually uses further from the eye.
 */
export function V2LegalPanel() {
  const { open: expanded, trigger, panel } = useDisclosure();
  const [openPolicyId, setOpenPolicyId] = useState<string | null>(null);

  const open = LEGAL_DOCUMENTS.find((policy) => policy.id === openPolicyId);

  return (
    <div data-testid="v2-legal-panel" className="text-sidebar-foreground">
      <button {...trigger} className={RAIL_BOX}>
        <Info aria-hidden />
        <span className="flex-1">Legal</span>
        <DisclosureChevron open={expanded} className="h-3 w-3" />
      </button>

      <ul {...panel}>
        {LEGAL_DOCUMENTS.map((policy) => (
          <li key={policy.id}>
            <button
              type="button"
              onClick={() => {
                setOpenPolicyId(policy.id);
              }}
              className="block w-full truncate rounded px-2 py-1.5 text-left text-xs text-white/60 transition-colors hover:bg-sidebar-accent hover:text-white"
            >
              {policy.document.title}
            </button>
          </li>
        ))}
      </ul>

      <PolicySheet
        policy={open}
        onClose={() => {
          setOpenPolicyId(null);
        }}
      />
    </div>
  );
}
